/**
 * A group's value, read-only (ORDR-119, item 4).
 *
 * A key and value list for an object; `null` shown as "(no value)"; a `<pre>`
 * JSON view for anything nested. A group whose whole value is `null` renders
 * as one "(no value)" line and NO list, so it can never look configured.
 */

import type { ReactElement } from "react";
import type { ConfigValue } from "./configApi.js";

export interface ValueViewProps {
  readonly value: ConfigValue | null;
  /** Only the keys this view is responsible for. */
  readonly keys?: (key: string) => boolean;
}

function Scalar({ value }: { readonly value: unknown }): ReactElement {
  if (value === null || value === undefined) {
    return <span className="italic">(no value)</span>;
  }
  if (typeof value === "object") {
    return <pre className="whitespace-pre-wrap text-xs">{JSON.stringify(value)}</pre>;
  }
  return <span>{String(value)}</span>;
}

export function ValueView({ value, keys = () => true }: ValueViewProps): ReactElement {
  const entries = value === null ? [] : Object.entries(value).filter(([key]) => keys(key));
  if (entries.length === 0) {
    return <p className="text-sm italic">(no value)</p>;
  }
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 text-sm">
      {entries.map(([key, entry]) => (
        <div key={key} className="contents">
          <dt className="font-mono">{key}</dt>
          <dd>
            <Scalar value={entry} />
          </dd>
        </div>
      ))}
    </dl>
  );
}
